import { Avatar } from "@dropbox/dig-components/avatar";
import { Button } from "@dropbox/dig-components/buttons";
import { Chip } from "@dropbox/dig-components/chip";
import { ClickOutside } from "@dropbox/dig-components/click_outside";
import { Menu } from "@dropbox/dig-components/menu";
import { Overlay } from "@dropbox/dig-components/overlay";
import { TextInput } from "@dropbox/dig-components/text_fields";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { FailFill } from "@dropbox/dig-icons/dist/mjs/assets";
import { PAP_Shown_DashInviteFormState } from "@mirage/analytics/events/types/shown_dash_invite_form_state";
import { getInitials } from "@mirage/shared/account";
import { useDebounce } from "@mirage/shared/hooks/useDebounce";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDashTeamInvitePapLogger } from "./hooks/useDashTeamInvitePapLogger";
import styles from "./InviteFormRow.module.css";
import { SuggestedTeamInvites } from "./SuggestedTeamInvites";
import type {
  EmailValidationResult,
  InviteFormContact,
  Role,
  TeamMember,
} from "./types";
import { getResultMessage } from "./utils/resultMessages";

import type { TextInputInputRefObject } from "@dropbox/dig-components/text_fields";
import { PAP_Input_DashInviteFormIndividual } from "@mirage/analytics/events/types/input_dash_invite_form_individual";
import { PAP_Select_DashInviteFormIndividualRole } from "@mirage/analytics/events/types/select_dash_invite_form_individual_role";

type Props = {
  teamMembers: Readonly<TeamMember[]>;
  disableRoleSelect: boolean;
  showRoleSelect: boolean;
  emailValidationResult?: EmailValidationResult;
  disabled?: boolean;
  onChange: (contact: InviteFormContact) => void;
  onClose: () => void;
  className?: string;
  preventClose?: boolean;
  isFssTeam: boolean;
};

export const InviteFormRow = ({
  teamMembers,
  disableRoleSelect,
  showRoleSelect,
  onChange,
  onClose,
  emailValidationResult,
  className,
  preventClose = false,
  disabled,
  isFssTeam,
}: Props) => {
  const [role, setRole] = useState<Role>("Member");
  const [chips, setChips] = useState<TeamMember[]>([]);
  const [typeaheadOpen, setTypeaheadOpen] = useState<boolean>(false);
  const [stringValue, setStringValue] = useState<string>("");
  const anchorRef = useRef(null);
  const inputRef = useRef<TextInputInputRefObject>(null);
  const [maxChipWidth, setMaxChipWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { logDashTeamInvitePapEvent } = useDashTeamInvitePapLogger();

  // When there is no validation result (null or undefined), there is no error or info message in UI
  // We do not see undefined (unvalidated) as error for UI purposes
  // When emailValidationResult is null, undefined, VALIDATION_PENDING, or VALID_EMAIL_TEAM_MEMBER_WITHOUT_DASH, there is no info or error message in UI
  const validationResultData = getResultMessage(
    emailValidationResult,
    isFssTeam,
  );
  const hasError = validationResultData.messageType === "error";
  const hasInfo = validationResultData.messageType === "info";

  // Variable used to control the outline color of the typeahead row
  // Row is disabled when submitting (which should override the blue outline to be subtle gray color)
  let typeaheadRowState;
  if (disabled) {
    typeaheadRowState = undefined;
  } else if (hasError) {
    typeaheadRowState = "error";
  } else if (hasInfo) {
    typeaheadRowState = "info";
  } else {
    typeaheadRowState = undefined;
  }

  // Controls whether remove row icon should show
  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  function normalizeString(str: string) {
    return str
      ?.normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }

  const normalizedInput = normalizeString(stringValue);

  // Check if input matches any team member name or email
  const matchedMembers = teamMembers.filter((member) => {
    const normalizedName = normalizeString(member.name || "");
    const normalizedEmail = normalizeString(member.email || "");

    return (
      normalizedName?.includes(normalizedInput) ||
      normalizedEmail?.includes(normalizedInput)
    );
  });

  const clearChip = () => {
    setStringValue("");
    setChips([]);
    setRole("Member");
  };

  useEffect(() => {
    setTypeaheadOpen(stringValue?.length > 0 && matchedMembers.length > 0);
  }, [stringValue, matchedMembers.length]);

  // Determine the width of the input field to set the max width of the chip
  // This prevents chips from overflowing the input field on smaller screen sizes
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setMaxChipWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Debounce function that updates chips/selection to prevent unnecessary calls to onSelectionChange
  const debouncedUpdateSelection = useDebounce((newChips: TeamMember[]) => {
    // Chip with no email fields for empty rows
    if (!newChips.length) {
      onChange({ email: null, newRole: role });
      return;
    }

    const matchedTeamMember = teamMembers.find(
      (member) => member.email === newChips[0].email,
    );

    if (matchedTeamMember) {
      const existingRole = matchedTeamMember.isAdmin ? "Co-admin" : "Member";
      setRole(existingRole);
      onChange({
        email: newChips[0].email,
        newRole: null,
      });
    } else {
      onChange({
        email: newChips[0].email,
        newRole: role,
      });
    }
  }, 300);

  useEffect(() => {
    debouncedUpdateSelection(chips);
  }, [chips, debouncedUpdateSelection]);

  const handleTeamMemberInputChange = useCallback((val: string) => {
    setStringValue(val);
  }, []);

  const handleTeamMemberInputBlur = useCallback(() => {
    if (chips.length) return;
    if (!stringValue) return;

    // Use an existing TeamMember if found, otherwise create a new one
    const existingTeamMember = teamMembers.find(
      (member) => member.email.toLowerCase() === stringValue.toLowerCase(),
    );
    const member = existingTeamMember || {
      email: stringValue,
      isAdmin: false,
    };

    setChips([member]);

    setIsFocused(false);
  }, [chips.length, stringValue, teamMembers]);

  const handleTeamMemberSelect = useCallback((teamMember: TeamMember) => {
    setTypeaheadOpen(false);
    setChips([teamMember]);
    setStringValue("");
  }, []);

  const handleClickOutside = () => {
    setStringValue("");
    setTypeaheadOpen(false);
  };

  const handleClickClose = () => {
    onClose();
  };

  const handleRoleSelection = useCallback(
    (val: Role) => {
      onChange({ email: chips[0]?.email, newRole: val });
      setRole(val);
      setIsFocused(false);
      logDashTeamInvitePapEvent(PAP_Select_DashInviteFormIndividualRole);
    },
    [onChange, chips, logDashTeamInvitePapEvent],
  );

  useEffect(() => {
    if (hasError) {
      logDashTeamInvitePapEvent(PAP_Shown_DashInviteFormState, {
        inviteFormErrorReason: validationResultData.loggedReason,
      });
    }
  }, [logDashTeamInvitePapEvent, hasError, validationResultData.loggedReason]);

  const logInputEmail = useCallback(() => {
    logDashTeamInvitePapEvent(PAP_Input_DashInviteFormIndividual);
  }, [logDashTeamInvitePapEvent]);

  return (
    <Box>
      <Box
        ref={containerRef}
        className={styles.textFieldContainer}
        display="flex"
        flexGrow={1}
        alignItems="center"
        onMouseOver={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      >
        <TextInput.Container
          ref={anchorRef}
          onClick={() => {
            // Focus on the input when the container is clicked
            // This enables clicks on the whitespace around a chip to focus the input
            inputRef.current?.focus();
          }}
          disabled={disabled}
        >
          <TextInput.ChipsContainer>
            {!!chips.length &&
              chips.map((chip, i) => (
                <Chip
                  key={`team-member-chip=${i}`}
                  size="small"
                  onDelete={clearChip}
                  variant={
                    hasError ? "alert" : hasInfo ? "attention" : "standard"
                  }
                  disabled={disabled}
                >
                  <Chip.Content>
                    <TeamMemberChip
                      showRoleSelect={showRoleSelect}
                      teamMember={chip}
                      maxChipWidth={maxChipWidth}
                    />
                  </Chip.Content>
                </Chip>
              ))}
            <TextInput.Input
              type="email"
              maxLength={255}
              aria-invalid={hasError}
              aria-errormessage={validationResultData.message ?? undefined}
              data-state={typeaheadRowState}
              disabled={chips?.length > 0 || disabled}
              className={classnames(
                styles.textField,
                styles.emailTextField,
                className,
                {
                  [styles.textFieldWithChips]: chips?.length > 0,
                },
              )}
              placeholder={
                !chips?.length ? i18n.t("team_invite_add_email_or_name") : ""
              }
              value={!chips.length ? stringValue : ""}
              onChange={(e) => handleTeamMemberInputChange(e.target.value)}
              onBlur={handleTeamMemberInputBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Tab") {
                  handleTeamMemberInputBlur();
                  handleClickOutside();
                }
              }}
              onFocus={logInputEmail}
              ref={inputRef}
            />
          </TextInput.ChipsContainer>
          <TextInput.Accessory>
            {showRoleSelect ? (
              <Menu.Wrapper
                isContentLabelledByTrigger
                onSelection={handleRoleSelection}
              >
                {({ getContentProps, getTriggerProps }) => (
                  <>
                    <Button
                      className={styles.menuButtonInput}
                      {...getTriggerProps()}
                      variant="opacity"
                      disabled={disableRoleSelect || disabled}
                      withDropdownIcon
                    >
                      {i18n.t(
                        role === "Member"
                          ? "team_invite_member"
                          : "team_invite_co_admin",
                      )}
                    </Button>
                    <Menu.Content {...getContentProps()}>
                      <Menu.Segment>
                        <Menu.ActionItem key="Member" value="Member">
                          {i18n.t("team_invite_member")}
                        </Menu.ActionItem>
                        <Menu.ActionItem key="Co-admin" value="Co-admin">
                          {i18n.t("team_invite_co_admin")}
                        </Menu.ActionItem>
                      </Menu.Segment>
                    </Menu.Content>
                  </>
                )}
              </Menu.Wrapper>
            ) : undefined}
          </TextInput.Accessory>
        </TextInput.Container>

        {/* Prevents deletion of first typeahead field in InviteForm which could result in no typeaheads being shown */}
        {!preventClose && !disabled && (
          <UIIcon
            src={FailFill}
            onClick={handleClickClose}
            className={classnames(styles.icon, {
              [styles.focused]: isHovering || isFocused,
            })}
          />
        )}

        {typeaheadOpen && (
          <ClickOutside
            isActive={typeaheadOpen}
            onClickOutside={handleClickOutside}
            isClickThroughPortaled={false}
          >
            <Overlay
              anchorRef={anchorRef}
              placement="bottom"
              setWidthSameAsAnchor={true}
              isPortaled={false}
            >
              <SuggestedTeamInvites
                teamMembers={matchedMembers}
                onTeamMemberSelect={handleTeamMemberSelect}
              />
            </Overlay>
          </ClickOutside>
        )}
      </Box>
      {validationResultData.message && hasError && (
        <Text variant="label" size="xsmall" color="error">
          {validationResultData.message}
        </Text>
      )}
      {validationResultData.message && hasInfo && (
        <Text
          variant="label"
          size="xsmall"
          style={{
            color:
              typeaheadRowState === "info"
                ? "var(--dig-color__primary__base)"
                : "var(--dig-color__disabled__base)",
          }}
        >
          {validationResultData.message}
        </Text>
      )}
    </Box>
  );
};

type TeamMemberChipProps = {
  teamMember: TeamMember;
  showRoleSelect: boolean;
  maxChipWidth: number;
};

const TeamMemberChip = ({
  teamMember,
  showRoleSelect,
  maxChipWidth,
}: TeamMemberChipProps) => {
  if (teamMember.name) {
    // Max width for truncating icons with long names depending on whether role selection icon is present
    const chipTextMaxWidth = Math.max(
      25,
      showRoleSelect ? maxChipWidth - 220 : maxChipWidth - 125,
    );
    return (
      <>
        <Avatar
          src={teamMember.photo}
          alt={teamMember.name}
          className={styles.avatar}
          size="small"
        >
          {getInitials(teamMember.name || teamMember.email || "")}
        </Avatar>
        <Truncate maxWidth={chipTextMaxWidth}>
          <Text>{teamMember.name}</Text>
        </Truncate>
      </>
    );
  }
  // Max width for truncating icons with long email inputs depending on whether role selection icon is present
  const chipTextMaxWidth = Math.max(
    25,
    showRoleSelect ? maxChipWidth - 200 : maxChipWidth - 110,
  );

  return (
    <Truncate maxWidth={chipTextMaxWidth}>
      <Text>{teamMember.email}</Text>
    </Truncate>
  );
};
